import { Button, Input } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { colors } from '@mm/utils';
import React, { useEffect, useState } from 'react';
import { useAudienceNameValidation } from '../../../editor/features/beta/modules/Audiences/use-audience-name-validation';
import { CheckIcon } from '../../component-icons/CheckIcon/CheckIcon';
import { ModalInstanceProps } from '../interfaces';
import { ModalBody, ModalFooter, ModalHeader } from '../modal-helpers';
import styles from './NewAudienceNamePromptModal.module.css';

type Props = ModalInstanceProps & {
  onConfirmHandler: (name: string) => void;
};

export const NewAudienceNamePromptModal = (props: Props) => {
  const { onConfirmHandler, closeModal } = props;

  const [newAudienceName, setNewAudienceName] = useState('');
  const trimmedNewAudienceName = newAudienceName.trim();
  const debouncedNewAudienceName = useDebounce(trimmedNewAudienceName, 250);
  const { validateAudienceName } = useAudienceNameValidation();
  const [valid, setValid] = useState(false);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    setValid(false);
    setChecking(!!trimmedNewAudienceName);
  }, [trimmedNewAudienceName]);
  useEffect(() => {
    (async () => {
      setChecking(true);
      const valid = await validateAudienceName(debouncedNewAudienceName);
      setValid(valid);
      setChecking(false);
    })();
  }, [debouncedNewAudienceName, validateAudienceName]);

  const onClickDone = () => {
    onConfirmHandler(trimmedNewAudienceName);
    closeModal();
  };

  return (
    <div>
      <ModalHeader newStyle>Name new Audience</ModalHeader>

      <ModalBody className={styles.modalBody}>
        <div>Give the audience a name so it's easy to find afterwards.</div>
        <Input
          css={{ container: styles.input }}
          value={newAudienceName}
          onChange={e => setNewAudienceName(e.target.value)}
          invalid={!valid && !checking}
          isLoading={checking}
          icon={
            valid && !checking && trimmedNewAudienceName ? (
              <CheckIcon color={colors.green} size={16} />
            ) : undefined
          }
          placeholder="Enter name here..."
        />
        {!valid && !checking && (
          <div className={styles.error}>
            An audience with that name already exists
          </div>
        )}
      </ModalBody>

      <ModalFooter newStyle>
        <Button color="secondary" onClick={closeModal}>
          Cancel
        </Button>

        <Button
          color="blue"
          disabled={!trimmedNewAudienceName || !valid || checking}
          onClick={onClickDone}
        >
          Done
        </Button>
      </ModalFooter>
    </div>
  );
};
